import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, map, tap } from 'rxjs';

import {
  FormFieldComponent,
  FormFieldErrorComponent,
} from '@aw/shared/components/form-field';
import { IconModule } from '@aw/shared/components/icon';
import { InputComponent } from '@aw/shared/components/input';
import {
  PhoneInputComponent,
  PhoneValidators,
} from '@aw/shared/components/phone-input';
import { strictEmailValidator } from '@aw/shared/validators';

import { ButtonComponent } from '@aw/prypco/components/button';
import { Module, Page } from '@aw/prypco/enums';
import { BlocksPage } from '@aw/prypco/modules/blocks/enum';
import { GtmService } from '@aw/prypco/services/script-manager';

import { OryAuthFacade } from '../../+state/ory-auth.facade';
import { OryAuthPages } from '../../constants/pages.constant';

@Component({
  selector: 'aw-ory-auth-register',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormFieldComponent,
    FormFieldErrorComponent,
    InputComponent,
    PhoneInputComponent,
    ButtonComponent,
    IconModule,
  ],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private gtmService = inject(GtmService);
  private oryAuthFacade = inject(OryAuthFacade);

  readonly loginRoute = ['/', OryAuthPages.ROOT, OryAuthPages.LOGIN];

  registerForm = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, strictEmailValidator()],
    }),
    firstName: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    lastName: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    phoneNumber: new FormControl('', {
      nonNullable: true,
      validators: [PhoneValidators.required, PhoneValidators.valid],
    }),
  });

  // Reactive state from store
  readonly flowId$ = this.oryAuthFacade.currentFlowId$;
  readonly csrftoken$ = this.oryAuthFacade.currentCsrfToken$;

  readonly isSubmitting$ = this.oryAuthFacade.isSubmitting$;
  readonly submitError$ = this.oryAuthFacade.submitError$;
  readonly session$ = this.oryAuthFacade.session$;

  readonly isRegistrationFlowLoading$ =
    this.oryAuthFacade.registrationFlowLoading$;
  readonly registrationFlowError$ = this.oryAuthFacade.registrationFlowError$;

  readonly error$ = combineLatest([
    this.registrationFlowError$,
    this.submitError$,
  ]).pipe(map(([flowError, submitError]) => flowError || submitError));

  readonly flowInit$ = this.route.queryParamMap.pipe(
    map((qpm) => qpm.get('flow')),
    tap((flowId) => {
      if (flowId) {
        this.oryAuthFacade.loadRegistrationFlow(flowId);
      } else {
        const returnTo =
          this.route.snapshot.queryParamMap.get('returnTo') ??
          `//${BlocksPage.TermsAndConditions}`;

        this.oryAuthFacade.startRegistrationFlow(returnTo);
      }
    }),
  );

  protected isSubmitting = toSignal(this.isSubmitting$, {
    initialValue: false,
  });
  protected submitError = toSignal(this.submitError$, {
    initialValue: null,
  });
  protected error = toSignal(this.error$);

  protected hasSession = toSignal(
    this.session$.pipe(map((session) => !!session)),
    { initialValue: false },
  );

  ngOnInit(): void {
    // Clear flow state and errors when navigating to register
    this.oryAuthFacade.clearFlowState();
    this.oryAuthFacade.clearErrors();
    this.flowInit$.subscribe();
  }

  submitForm(): void {
    if (this.registerForm.valid && !this.isSubmitting() && !this.hasSession()) {
      this.oryAuthFacade.submitRegistration(this.registerForm.getRawValue());
    }
  }

  goBack(): void {
    this.location.back();
  }

  onLogin() {
    this.gtmService.pushEvent(
      {
        event: 'user_login_started',
        click_text: 'Login',
        cta_location: 'register_page',
        page_url: window.location.href.toString(),
        timestamp: new Date().toISOString(),
      },
      false,
    );
  }
}
